import React, { useState, useEffect, useRef, useCallback } from "react";
import "./header.css";
import { FaWhatsapp, FaBars, FaTimes } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import logo from '../../assets/suvidha elder care logo .png';

const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://suvidha-backend-353j.onrender.com";

function authHeaders() {
  const t = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const TYPE_ICON = {
  booking_confirmed:        "🎉",
  booking_payment_received: "✅",
  subscription_activated:   "💳",
  new_booking:              "🛠",
  new_subscription:         "💳",
};

// ── Notification Bell (user) ──────────────────────────────
function UserNotifBell({ onClose: closeOthers }) {
  const [open,    setOpen]    = useState(false);
  const [notifs,  setNotifs]  = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [loading, setLoading] = useState(false);
  const ref      = useRef(null);
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem("token");

  const load = useCallback(async () => {
    if (!localStorage.getItem("token")) return;
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/notifications`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch(e) {}
    finally { setLoading(false); }
  }, []);

  // Initial load + poll every 30s
  useEffect(() => {
    if (!isLoggedIn) return;
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load, isLoggedIn]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!isLoggedIn) return null;

  const markAll = async () => {
    try {
      await fetch(`${BASE_URL}/api/notifications/read-all`, { method: "POST", headers: authHeaders() });
      setNotifs(n => n.map(x => ({ ...x, is_read: 1 })));
      setUnread(0);
    } catch(e) {}
  };

  const clickNotif = async (n) => {
    if (!n.is_read) {
      try {
        await fetch(`${BASE_URL}/api/notifications/${n.id}/read`, { method: "POST", headers: authHeaders() });
        setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
        setUnread(u => Math.max(0, u - 1));
      } catch(e) {}
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const fmtTime = (d) => {
    const diff = Math.floor((Date.now() - new Date(d)) / 1000);
    if (diff < 60)    return "just now";
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const toggleOpen = () => {
    closeOthers(); // close user-menu dropdown when bell opens
    setOpen(o => !o);
    if (!open) load();
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      {/* Bell button */}
      <button onClick={toggleOpen} style={{
        background: "none", border: "none", cursor: "pointer",
        fontSize: 22, position: "relative", padding: "4px 8px",
        color: "#004F9E", display: "flex", alignItems: "center"
      }} title="Notifications">
        🔔
        {unread > 0 && (
          <span style={{
            position: "absolute", top: 0, right: 2,
            background: "#DC2626", color: "#fff",
            fontSize: 9, fontWeight: 900,
            width: 16, height: 16, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid #fff", lineHeight: 1
          }}>{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "fixed",
        top: 68, right: 8,
        width: "min(340px, calc(100vw - 16px))",
        maxHeight: "min(420px, calc(100vh - 80px))",
        background: "#fff", borderRadius: 16,
        boxShadow: "0 20px 60px rgba(0,0,0,.18)",
        border: "1px solid #E2E8F0",
        zIndex: 9999, overflow: "hidden",
        display: "flex", flexDirection: "column"
        }}>
          {/* Header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 16px 10px", borderBottom: "1px solid #F1F5F9"
          }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: "#1E293B" }}>
              🔔 Notifications
              {unread > 0 && (
                <span style={{
                  marginLeft: 6, background: "#DC2626", color: "#fff",
                  fontSize: 10, fontWeight: 800,
                  padding: "2px 6px", borderRadius: 999
                }}>{unread}</span>
              )}
            </span>
            {unread > 0 && (
              <button onClick={markAll} style={{
                fontSize: 12, color: "#004F9E", fontWeight: 700,
                background: "none", border: "none", cursor: "pointer",
                fontFamily: "inherit"
              }}>Mark all read</button>
            )}
          </div>

          {/* Notification list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                Loading...
              </div>
            ) : notifs.length === 0 ? (
              <div style={{ padding: 36, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🔕</div>
                <div style={{ color: "#94A3B8", fontSize: 13, fontWeight: 600 }}>No notifications yet</div>
                <div style={{ color: "#CBD5E1", fontSize: 12, marginTop: 4 }}>
                  You'll be notified about bookings & subscriptions
                </div>
              </div>
            ) : (
              notifs.map(n => (
                <div key={n.id} onClick={() => clickNotif(n)} style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "12px 16px",
                  background: n.is_read ? "#fff" : "#EFF6FF",
                  borderBottom: "1px solid #F8FAFC",
                  cursor: "pointer",
                }}>
                  {/* Icon bubble */}
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: n.is_read ? "#F1F5F9" : "#DBEAFE",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16
                  }}>
                    {TYPE_ICON[n.type] || "📢"}
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: n.is_read ? 600 : 800,
                      fontSize: 13, color: "#1E293B", marginBottom: 2
                    }}>{n.title}</div>
                    <div style={{
                      fontSize: 12, color: "#64748B",
                      lineHeight: 1.5, wordBreak: "break-word"
                    }}>{n.message}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
                      {fmtTime(n.created_at)}
                    </div>
                  </div>

                  {/* Unread dot */}
                  {!n.is_read && (
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: "#004F9E", flexShrink: 0, marginTop: 5
                    }}/>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Header ───────────────────────────────────────────
const Header = () => {
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate    = useNavigate();

  const user        = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const isLoggedIn  = !!localStorage.getItem("token");
  const isAdmin     = user?.role === "admin" || user?.role === "super_admin";
  const firstName   = user?.full_name?.split(" ")[0] || "Account";

  const phoneNumber = "919390683569";
  const message     = "Hello, I want to know more about your services";

  // Close user menu on outside click
  useEffect(() => {
    const h = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUserMenuOpen(false);
    navigate("/login");
  };

  return (
    <>
      <header className="main-header">
        <div className="container">

          {/* Logo */}
          <div className="logo">
            <Link to="/" className="logo-text">
              <img src={logo} alt="Suvidha" />
            </Link>
          </div>

          {/* Nav */}
          <nav className={`nav-menu ${menuOpen ? "active" : ""}`}>
            <ul>
              <li><Link to="/" onClick={() => setMenuOpen(false)}>Home</Link></li>
              <li><Link to="/about-us" onClick={() => setMenuOpen(false)}>About-Us</Link></li>

              <li><Link to="/services" onClick={() => setMenuOpen(false)}>Our Services</Link></li>
              <li><Link to="/plans"    onClick={() => setMenuOpen(false)}>Plans</Link></li>
              <li><Link to="/blogs"    onClick={() => setMenuOpen(false)}>Blogs</Link></li>
              <li><Link to="/Contact"  onClick={() => setMenuOpen(false)}>Contact</Link></li>
            </ul>
          </nav>

          {/* Right side */}
          <div className="header-right">

            {/* WhatsApp */}
            <FaWhatsapp
              className="whatsapp-icon"
              onClick={() => window.open(
                `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, "_blank"
              )}
              style={{ cursor: "pointer", fontSize: "28px", color: "#25D366" }}
            />

            {isLoggedIn ? (
              <>
                {/* 🔔 Notification Bell */}
                <UserNotifBell onClose={() => setUserMenuOpen(false)} />

                {/* 👤 User Menu */}
                <div ref={userMenuRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setUserMenuOpen(o => !o)}
                    className="login-btn"
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    👤 <span className="user-name-label">{firstName}</span> <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
                  </button>

                  {userMenuOpen && (
                    <div style={{
                      position: "absolute", right: 0, top: "calc(100% + 10px)",
                      background: "#fff", border: "1px solid #E2E8F0",
                      borderRadius: 14, boxShadow: "0 16px 48px rgba(0,0,0,.14)",
                      minWidth: 210, zIndex: 9999, padding: "6px 0",
                      animation: "fadeIn .15s ease"
                    }}>
                      {/* User info */}
                      <div style={{
                        padding: "10px 18px 8px",
                        borderBottom: "1px solid #F1F5F9",
                        fontSize: 13, color: "#94A3B8"
                      }}>
                        <div style={{ fontWeight: 800, color: "#1E293B", fontSize: 14 }}>
                          {user?.full_name}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 2 }}>{user?.phone}</div>
                      </div>

                      {isAdmin && (
                        <Link to="/admin/dashboard" onClick={() => setUserMenuOpen(false)} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 18px", fontSize: 14,
                          color: "#004F9E", textDecoration: "none", fontWeight: 700
                        }}>
                          🖥️ Admin Dashboard
                        </Link>
                      )}

                      <Link to="/my-bookings" onClick={() => setUserMenuOpen(false)} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 18px", fontSize: 14,
                        color: "#374151", textDecoration: "none"
                      }}>📋 My Bookings</Link>

                      <Link to="/my-subscription" onClick={() => setUserMenuOpen(false)} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 18px", fontSize: 14,
                        color: "#374151", textDecoration: "none"
                      }}>💳 My Subscription</Link>

                      <Link to="/book-service" onClick={() => setUserMenuOpen(false)} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 18px", fontSize: 14,
                        color: "#374151", textDecoration: "none"
                      }}>🛠 Book a Service</Link>

                      <Link to="/survey/care_needs?stepNo=1&retake=1" onClick={async () => {
                        setUserMenuOpen(false);
                        // Reset survey_done so user can retake
                        try {
                          const token = localStorage.getItem("token");
                          await fetch(`${BASE_URL}/api/users/survey-reset`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
                          });
                          // Update local user state
                          const u = JSON.parse(localStorage.getItem("user") || "{}");
                          u.survey_done = false;
                          localStorage.setItem("user", JSON.stringify(u));
                        } catch(e) {}
                      }} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 18px", fontSize: 14,
                        color: "#F17A00", textDecoration: "none", fontWeight: 600
                      }}>📋 Update My Survey</Link>

                      <div style={{ borderTop: "1px solid #F1F5F9", margin: "4px 0" }} />

                      <button onClick={handleLogout} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        width: "100%", padding: "10px 18px",
                        fontSize: 14, color: "#DC2626",
                        background: "none", border: "none",
                        cursor: "pointer", textAlign: "left",
                        fontWeight: 700, fontFamily: "inherit"
                      }}>🚪 Logout</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link to="/login">
                <button className="login-btn">Log in</button>
              </Link>
            )}

            {/* Hamburger */}
            <div className="hamburger" onClick={() => setMenuOpen(m => !m)}>
              {menuOpen ? <FaTimes /> : <FaBars />}
            </div>

          </div>
        </div>
      </header>
    </>
  );
};

export default Header;